<template>
  <div class="dash-chart" :class="{ 'dash-chart--empty': !points.length && !loading }">
    <div v-if="loading" class="dash-chart__skeleton" aria-hidden="true" />

    <template v-else-if="points.length">
      <svg
        class="dash-chart__svg"
        :viewBox="`0 0 ${width} ${height}`"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="dash-chart-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="rgba(30, 77, 255, 0.45)" />
            <stop offset="100%" stop-color="rgba(108, 99, 255, 0.02)" />
          </linearGradient>
        </defs>
        <path :d="areaPath" class="dash-chart__area" fill="url(#dash-chart-grad)" />
        <path :d="linePath" class="dash-chart__line" />
      </svg>

      <div class="dash-chart__labels">
        <span
          v-for="(point, index) in labelPoints"
          :key="index"
          class="dash-chart__label"
        >
          {{ point.label }}
        </span>
      </div>
    </template>

    <p v-else class="dash-chart__empty-text">No chart data for this period.</p>
  </div>
</template>

<script setup>
import { computed } from 'vue'

defineOptions({ name: 'DashboardChart' })

const props = defineProps({
  points: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const width = 800
const height = 220
const padX = 12
const padY = 16

const values = computed(() => props.points.map((p) => p.value))
const maxValue = computed(() => Math.max(...values.value, 1))

const coords = computed(() => {
  const pts = props.points
  if (!pts.length) return []
  const innerW = width - padX * 2
  const innerH = height - padY * 2
  return pts.map((p, i) => {
    const x = padX + (pts.length === 1 ? innerW / 2 : (innerW * i) / (pts.length - 1))
    const y = padY + innerH - (p.value / maxValue.value) * innerH
    return { x, y }
  })
})

const linePath = computed(() => {
  const c = coords.value
  if (!c.length) return ''
  return c.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
})

const areaPath = computed(() => {
  const c = coords.value
  if (!c.length) return ''
  const base = height - padY
  const start = `M ${c[0].x} ${base}`
  const line = c.map((p) => `L ${p.x} ${p.y}`).join(' ')
  const end = `L ${c[c.length - 1].x} ${base} Z`
  return `${start} ${line} ${end}`
})

const labelPoints = computed(() => {
  const pts = props.points
  if (pts.length <= 6) return pts
  const step = Math.ceil(pts.length / 6)
  return pts.filter((_, i) => i % step === 0 || i === pts.length - 1)
})
</script>
